import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { SharingStats, SharedResource, ShareFilterType } from '@/types/sharing';

export function useSharing(filter: ShareFilterType = 'all') {
  const { user } = useAuth();

  // Estatísticas de compartilhamento
  const { data: stats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['sharing-stats', user?.id],
    queryFn: async (): Promise<SharingStats> => {
      if (!user) throw new Error('User not authenticated');

      // ✅ Contar TOTAL de compartilhamentos (pessoas) - não recursos únicos
      const { count: casoSharesCount } = await supabase
        .from('caso_user_shares')
        .select('*', { count: 'exact', head: true })
        .eq('shared_by_user_id', user.id);

      const { count: chatSharesCount } = await supabase
        .from('conversation_user_shares')
        .select('*', { count: 'exact', head: true })
        .eq('shared_by_user_id', user.id);

      // Para estatísticas de recursos únicos (manter para "Casos Compartilhados" e "Chats Compartilhados")
      const { data: casoShares } = await supabase
        .from('caso_user_shares')
        .select('caso_id, shared_with_user_id')
        .eq('shared_by_user_id', user.id);

      const uniqueCasoIds = new Set(casoShares?.map(s => s.caso_id) || []);

      const { data: chatShares } = await supabase
        .from('conversation_user_shares')
        .select('conversation_id, shared_with_user_id')
        .eq('shared_by_user_id', user.id);

      const uniqueChatIds = new Set(chatShares?.map(s => s.conversation_id) || []);

      // Contar usuários ÚNICOS com quem compartilhou (membros ativos)
      const sharedWithIds = new Set([
        ...(casoShares || []).map(s => s.shared_with_user_id),
        ...(chatShares || []).map(s => s.shared_with_user_id),
      ]);

      // Buscar última atividade de compartilhamento
      const { data: lastCasoShare } = await supabase
        .from('caso_user_shares')
        .select('shared_at')
        .eq('shared_by_user_id', user.id)
        .order('shared_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const { data: lastChatShare } = await supabase
        .from('conversation_user_shares')
        .select('shared_at')
        .eq('shared_by_user_id', user.id)
        .order('shared_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const lastActivity = [lastCasoShare?.shared_at, lastChatShare?.shared_at]
        .filter(Boolean)
        .sort()
        .reverse()[0];

      return {
        casosCompartilhados: uniqueCasoIds.size,
        chatsCompartilhados: uniqueChatIds.size,
        membrosAtivos: sharedWithIds.size,
        totalCompartilhamentos: (casoSharesCount || 0) + (chatSharesCount || 0), // ✅ Total de PESSOAS, não recursos
        ultimaAtividade: lastActivity ? new Date(lastActivity) : null,
      };
    },
    enabled: !!user,
  });

  // Recursos compartilhados
  const { data: resources, isLoading: isLoadingResources } = useQuery({
    queryKey: ['shared-resources', user?.id, filter],
    queryFn: async (): Promise<SharedResource[]> => {
      if (!user) throw new Error('User not authenticated');

      const sharedResources: SharedResource[] = [];

      // Buscar casos compartilhados (tabela correta: caso_user_shares)
      if (filter === 'all' || filter === 'casos') {
        // Buscar caso_ids únicos
        const { data: uniqueCasos } = await supabase
          .from('caso_user_shares')
          .select('caso_id')
          .eq('shared_by_user_id', user.id);

        const uniqueCasoIds = [...new Set(uniqueCasos?.map(s => s.caso_id) || [])];

        for (const casoId of uniqueCasoIds) {
          // Buscar TODOS os compartilhamentos deste caso
          const { data: allShares } = await supabase
            .from('caso_user_shares')
            .select(`
              caso_id,
              shared_by_user_id,
              shared_with_user_id,
              shared_at,
              expires_at,
              permission_level,
              casos!inner(id, title)
            `)
            .eq('caso_id', casoId)
            .eq('shared_by_user_id', user.id)
            .order('shared_at', { ascending: true });

          if (!allShares || allShares.length === 0) continue;

          // Buscar nomes de TODOS os usuários
          const userIds = allShares.map(s => s.shared_with_user_id);
          const { data: sharedUsers } = await supabase
            .from('users')
            .select('id, display_name, email')
            .in('id', userIds);

          const userNames = sharedUsers?.map(u => u.display_name || u.email || 'Usuário') || [];

          // Get current user display name
          const { data: currentUserData } = await supabase
            .from('users')
            .select('display_name')
            .eq('id', user.id)
            .single();

          sharedResources.push({
            id: casoId,
            type: 'caso',
            title: (allShares[0].casos as any).title,
            resourceId: casoId,
            sharedBy: {
              id: user.id,
              name: currentUserData?.display_name || user.email || 'Você',
              email: user.email || '',
            },
            sharedWith: 'specific_members',
            firmId: '',
            firmName: userNames.join(', '),
            memberCount: userNames.length,
            memberNames: userNames,
            sharedAt: new Date(allShares[0].shared_at),
            expiresAt: allShares[0].expires_at ? new Date(allShares[0].expires_at) : null,
            canManage: true,
          });
        }
      }

      // Buscar chats compartilhados (tabela correta: conversation_user_shares)
      if (filter === 'all' || filter === 'chats') {
        // Buscar conversation_ids únicos
        const { data: uniqueConvs } = await supabase
          .from('conversation_user_shares')
          .select('conversation_id')
          .eq('shared_by_user_id', user.id);

        const uniqueConvIds = [...new Set(uniqueConvs?.map(s => s.conversation_id) || [])];

        for (const convId of uniqueConvIds) {
          // Buscar TODOS os compartilhamentos desta conversa
        const { data: allShares } = await supabase
          .from('conversation_user_shares')
          .select(`
            conversation_id,
            shared_by_user_id,
            shared_with_user_id,
            shared_at,
            expires_at,
            permission_level,
            legal_conversations!inner(id, title)
          `)
          .eq('conversation_id', convId)
          .eq('shared_by_user_id', user.id)
          .order('shared_at', { ascending: true });

          if (!allShares || allShares.length === 0) continue;

          // Buscar nomes de TODOS os usuários
          const userIds = allShares.map(s => s.shared_with_user_id);
          const { data: sharedUsers } = await supabase
            .from('users')
            .select('id, display_name, email')
            .in('id', userIds);

          const userNames = sharedUsers?.map(u => u.display_name || u.email || 'Usuário') || [];

          // Get current user display name
          const { data: currentUserData } = await supabase
            .from('users')
            .select('display_name')
            .eq('id', user.id)
            .single();

        sharedResources.push({
          id: convId,
          type: 'chat',
          title: (allShares[0].legal_conversations as any).title,
          resourceId: convId,
          sharedBy: {
            id: user.id,
            name: currentUserData?.display_name || user.email || 'Você',
            email: user.email || '',
          },
          sharedWith: 'specific_members',
          firmId: '',
          firmName: userNames.join(', '),
          memberCount: userNames.length,
          memberNames: userNames,
          sharedAt: new Date(allShares[0].shared_at),
          expiresAt: allShares[0].expires_at ? new Date(allShares[0].expires_at) : null,
          canManage: true,
        });
        }
      }

      return sharedResources.sort((a, b) => 
        b.sharedAt.getTime() - a.sharedAt.getTime()
      );
    },
    enabled: !!user,
  });

  return {
    stats,
    resources,
    isLoading: isLoadingStats || isLoadingResources,
  };
}
